import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	ArrowDownToLine,
	ClipboardCopy,
	ClipboardPaste,
	FileSpreadsheet,
	Loader2,
	Plus,
	Rows3,
	Save,
	Send,
	Trash2,
	Undo2,
} from 'lucide-react';
import { apiService } from '../../services/api';
import {
	NoPayrollAccess,
	full,
	fyOf,
	href,
	monthLabel,
	nowYm,
	shiftMonth,
	usePayrollAccess,
} from './shared';

/**
 * Salary Sheets — the salary sheet, typed or pasted, in a screen that looks and works like Excel.
 * Save keeps a draft. Submit makes it the final sheet for the site and month, and locks it.
 * Gross and net are worked out (shown as formulas), never typed. Backend: payrollEntry.service.js.
 *
 * Excel keys: click + type replaces, F2 / double-click edits, Enter / Tab / arrows move,
 * Shift or drag selects, Ctrl+C / Ctrl+V / Ctrl+Z / Delete work on the selection.
 *
 * ponytail: no approval and no per-site access yet; add with the access rules.
 */

type Row = Record<string, string>;
interface Entry {
	status: 'new' | 'draft' | 'submitted';
	saved_at: string | null;
	copied_from: string | null;
	rows: Record<string, string | number | null>[];
}
interface Col {
	key: string;
	title: string;
	w: number;
	num?: boolean;
	calc?: boolean;
}

const COLS: Col[] = [
	{ key: 'emp_id', title: 'Emp ID', w: 80 },
	{ key: 'person_name', title: 'Name', w: 190 },
	{ key: 'designation', title: 'Designation', w: 120 },
	{ key: 'working_days', title: 'Days', w: 56, num: true },
	{ key: 'basic', title: 'Basic', w: 80, num: true },
	{ key: 'da', title: 'DA', w: 72, num: true },
	{ key: 'hra', title: 'HRA', w: 72, num: true },
	{ key: 'special_allowance', title: 'Special', w: 72, num: true },
	{ key: 'overtime', title: 'Overtime', w: 76, num: true },
	{ key: 'other_earnings', title: 'Other earn', w: 80, num: true },
	{ key: 'pf_ee', title: 'PF', w: 68, num: true },
	{ key: 'esi_ee', title: 'ESI', w: 64, num: true },
	{ key: 'pt', title: 'PT', w: 56, num: true },
	{ key: 'tds', title: 'TDS', w: 64, num: true },
	{ key: 'advance', title: 'Advance', w: 72, num: true },
	{ key: 'other_deductions', title: 'Other ded', w: 80, num: true },
	{ key: 'gross', title: 'Gross', w: 90, num: true, calc: true },
	{ key: 'net_pay', title: 'Net pay', w: 96, num: true, calc: true },
	{ key: 'bank_account', title: 'Bank account', w: 130 },
	{ key: 'ifsc', title: 'IFSC', w: 100 },
	{ key: 'uan', title: 'UAN', w: 110 },
	{ key: 'esic_no', title: 'ESIC no', w: 110 },
	{ key: 'remarks', title: 'Remarks', w: 180 },
];
const INPUT = COLS.filter(c => !c.calc);
const EARN = ['basic', 'da', 'hra', 'special_allowance', 'overtime', 'other_earnings'];
const DEDUCT = ['pf_ee', 'esi_ee', 'pt', 'tds', 'advance', 'other_deductions'];
const idx = (k: string) => COLS.findIndex(c => c.key === k);
const letter = (i: number): string =>
	i < 26 ? String.fromCharCode(65 + i) : letter(Math.floor(i / 26) - 1) + letter(i % 26);
// Excel row numbers: row 1 is the header, people start at row 2.
const xlRow = (r: number) => r + 2;

const n = (v: string | undefined) => (v && !isNaN(Number(v)) ? Number(v) : 0);
const gross = (r: Row) => EARN.reduce((a, k) => a + n(r[k]), 0);
const net = (r: Row) => gross(r) - DEDUCT.reduce((a, k) => a + n(r[k]), 0);
const general = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(2));
const blank = (): Row => ({});
const isEmpty = (r: Row) => INPUT.every(c => !(r[c.key] || '').trim());
const toRow = (r: Entry['rows'][number]): Row =>
	Object.fromEntries(
		INPUT.map(c => [c.key, r[c.key] === null || r[c.key] === undefined ? '' : String(r[c.key])])
	);
const badNum = (v: string | undefined) => !!v && (isNaN(Number(v)) || Number(v) < 0);
const cleanNum = (v: string) => v.replace(/[,₹\s]/g, '');
const draftKey = (site: string, month: string) => `payroll-entry|${site}|${month}`;
// Like Excel, the sheet always has empty rows below the data to move into. Only filled rows are saved.
const pad = (rs: Row[]) => {
	let last = rs.length - 1;
	while (last >= 0 && isEmpty(rs[last])) last--;
	const want = Math.max(60, last + 31);
	return rs.length >= want ? rs : [...rs, ...Array.from({ length: want - rs.length }, blank)];
};

/** What a cell shows, and what the formula bar shows for it. */
const shown = (r: Row, c: Col) => {
	if (c.key === 'gross') return isEmpty(r) ? '' : general(gross(r));
	if (c.key === 'net_pay') return isEmpty(r) ? '' : general(net(r));
	return r[c.key] || '';
};
const formula = (ri: number, c: Col) => {
	const R = xlRow(ri);
	const range = (a: string, b: string) => `${letter(idx(a))}${R}:${letter(idx(b))}${R}`;
	if (c.key === 'gross') return `=SUM(${range('basic', 'other_earnings')})`;
	if (c.key === 'net_pay')
		return `=${letter(idx('gross'))}${R}-SUM(${range('pf_ee', 'other_deductions')})`;
	return null;
};

// Excel 365 look.
const XL = {
	green: '#217346',
	grid: '#e1e1e1',
	headBg: '#f3f3f3',
	headOn: '#d3f0e0',
	font: 'Calibri, Carlito, "Segoe UI", Arial, sans-serif',
};
const ROW_H = 21;
const NUM_W = 42;

const RibbonBtn: React.FC<{
	icon: React.ElementType;
	label: string;
	onClick: () => void;
	disabled?: boolean;
	why?: string | null;
	big?: boolean;
}> = ({ icon: Icon, label, onClick, disabled, why, big }) => (
	<button
		onClick={onClick}
		disabled={disabled}
		title={disabled && why ? why : label}
		className={`flex flex-col items-center justify-center gap-0.5 px-2 rounded hover:bg-[#e5e5e5] disabled:opacity-40 disabled:hover:bg-transparent ${big ? 'min-w-[58px] h-[62px]' : 'min-w-[48px] h-[62px]'}`}
	>
		<Icon className={big ? 'w-7 h-7' : 'w-5 h-5'} style={{ color: XL.green }} strokeWidth={1.6} />
		<span className='text-[12px] leading-tight text-[#333]'>{label}</span>
	</button>
);

const Group: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div className='flex flex-col items-stretch px-2 border-r border-[#d6d6d6] last:border-r-0'>
		<div className='flex items-center gap-1 flex-1'>{children}</div>
		<div className='text-[11px] text-[#666] text-center pt-0.5'>{label}</div>
	</div>
);

const SalaryEntry: React.FC = () => {
	const [sites, setSites] = useState<string[]>([]);
	// Sheets added with "+" this session; they become real once saved.
	const [added, setAdded] = useState<string[]>([]);
	const allSites = [...sites, ...added.filter(a => !sites.includes(a))];
	const [site, setSite] = useState('');
	const [month, setMonth] = useState(shiftMonth(nowYm, -1));
	const [entry, setEntry] = useState<Entry | null>(null);
	const [rows, setRows] = useState<Row[]>(() => pad([]));
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [msg, setMsg] = useState<{ bad: boolean; text: string } | null>(null);
	const [restored, setRestored] = useState(false);

	// Active cell, the other corner of the selection, and edit state — like Excel.
	const [act, setAct] = useState({ r: 0, c: 1 });
	const [end, setEnd] = useState({ r: 0, c: 1 });
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState('');
	const [fromBar, setFromBar] = useState(false);
	const editingRef = useRef(false);
	const dragging = useRef(false);
	const history = useRef<Row[][]>([]);
	const sheet = useRef<HTMLDivElement>(null);

	useEffect(() => {
		apiService
			.get<string[]>('/payroll/entry/sites')
			.then(r => {
				const list = r.data || [];
				setSites(list);
				// Open on the first site, like a workbook opens on its first sheet.
				setSite(cur => cur || list[0] || '');
			})
			.catch(() => setSites([]));
		const up = () => (dragging.current = false);
		window.addEventListener('mouseup', up);
		return () => window.removeEventListener('mouseup', up);
	}, []);

	useEffect(() => {
		if (!site || !month) return;
		let live = true;
		setLoading(true);
		setMsg(null);
		apiService
			.get<Entry>(`/payroll/entry?site=${encodeURIComponent(site)}&month=${month}`)
			.then(r => {
				if (!live) return;
				const e = r.data;
				setEntry(e);
				// UI rule 4: typed work survives a refresh until it is saved.
				const kept = e.status !== 'submitted' && sessionStorage.getItem(draftKey(site, month));
				setRestored(!!kept);
				const base = kept ? (JSON.parse(kept) as Row[]) : e.rows.map(toRow);
				setRows(pad(base));
				history.current = [];
				setAct({ r: 0, c: 1 });
				setEnd({ r: 0, c: 1 });
			})
			.catch(err => live && setMsg({ bad: true, text: err?.message || 'Could not open the sheet' }))
			.finally(() => live && setLoading(false));
		return () => {
			live = false;
		};
	}, [site, month]);

	const locked = entry?.status === 'submitted';
	const ready = !!site && !!entry && !loading;
	const canEdit = ready && !locked;
	const filled = rows.filter(r => !isEmpty(r));
	let lastFilled = rows.length - 1;
	while (lastFilled > 0 && isEmpty(rows[lastFilled])) lastFilled--;

	const update = useCallback(
		(next: Row[], remember = true) => {
			if (remember) history.current = [...history.current.slice(-99), rows];
			setRows(pad(next.map(r => r || blank())));
			sessionStorage.setItem(
				draftKey(site, month),
				JSON.stringify(next.filter(r => r && !isEmpty(r)))
			);
		},
		[rows, site, month]
	);

	const box = {
		r1: Math.min(act.r, end.r),
		r2: Math.max(act.r, end.r),
		c1: Math.min(act.c, end.c),
		c2: Math.max(act.c, end.c),
	};
	const inBox = (r: number, c: number) => r >= box.r1 && r <= box.r2 && c >= box.c1 && c <= box.c2;
	const many = box.r1 !== box.r2 || box.c1 !== box.c2;

	const focusSheet = () => sheet.current?.focus({ preventScroll: true });
	const reveal = (r: number, c: number) =>
		requestAnimationFrame(() =>
			sheet.current
				?.querySelector(`[data-cell="${r}-${c}"]`)
				?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
		);
	const go = (r: number, c: number, extend = false) => {
		// Moving near the bottom adds more empty rows, so Enter never gets stuck.
		if (r >= rows.length - 5) setRows(rs => [...rs, ...Array.from({ length: 30 }, blank)]);
		const p = {
			r: Math.max(0, Math.min(rows.length + 29, r)),
			c: Math.max(0, Math.min(COLS.length - 1, c)),
		};
		if (extend) setEnd(p);
		else {
			setAct(p);
			setEnd(p);
		}
		reveal(p.r, p.c);
	};

	const startEdit = (value: string, bar = false) => {
		if (!canEdit || COLS[act.c].calc) return;
		setEnd(act);
		setFromBar(bar);
		setDraft(value);
		setEditing(true);
		editingRef.current = true;
	};
	const commit = (move?: { dr: number; dc: number }) => {
		if (!editingRef.current) return;
		editingRef.current = false;
		setEditing(false);
		const c = COLS[act.c];
		const v = c.num ? cleanNum(draft) : draft;
		if (v !== (rows[act.r]?.[c.key] || ''))
			update(rows.map((r, i) => (i === act.r ? { ...r, [c.key]: v } : r)));
		if (move) go(act.r + move.dr, act.c + move.dc);
		focusSheet();
	};
	const cancel = () => {
		editingRef.current = false;
		setEditing(false);
		focusSheet();
	};

	const clearBox = () => {
		if (!canEdit) return;
		// Whole rows selected (row numbers clicked): remove them. Otherwise clear the cells, like Excel.
		if (box.c1 === 0 && box.c2 === COLS.length - 1) {
			update(rows.filter((_, i) => i < box.r1 || i > box.r2));
			setAct({ r: box.r1, c: 1 });
			setEnd({ r: box.r1, c: 1 });
			return;
		}
		update(
			rows.map((r, ri) => {
				if (ri < box.r1 || ri > box.r2) return r;
				const o = { ...r };
				for (let c = box.c1; c <= box.c2; c++) if (!COLS[c].calc) o[COLS[c].key] = '';
				return o;
			})
		);
	};
	const copyBox = () => {
		const text = rows
			.slice(box.r1, box.r2 + 1)
			.map(r =>
				COLS.slice(box.c1, box.c2 + 1)
					.map(c => shown(r, c))
					.join('\t')
			)
			.join('\n');
		navigator.clipboard?.writeText(text).catch(() => undefined);
		setMsg({ bad: false, text: `Copied ${box.r2 - box.r1 + 1} × ${box.c2 - box.c1 + 1} cells` });
	};
	const pasteText = (text: string) => {
		if (!canEdit) return;
		const lines = text
			.replace(/\r/g, '')
			.replace(/\n$/, '')
			.split('\n')
			.map(l => l.split('\t'));
		const next = rows.slice();
		const put = (ri: number, ci: number, v: string) => {
			const c = COLS[ci];
			if (!c || c.calc) return;
			next[ri] = { ...(next[ri] || blank()), [c.key]: c.num ? cleanNum(v) : v.trim() };
		};
		// One value over a selection fills the selection, like Excel.
		if (lines.length === 1 && lines[0].length === 1 && many) {
			for (let r = box.r1; r <= box.r2; r++)
				for (let c = box.c1; c <= box.c2; c++) put(r, c, lines[0][0]);
		} else lines.forEach((cells, dy) => cells.forEach((v, dx) => put(box.r1 + dy, box.c1 + dx, v)));
		update(next);
		setEnd({
			r: box.r1 + lines.length - 1,
			c: Math.min(COLS.length - 1, box.c1 + lines[0].length - 1),
		});
		setMsg({ bad: false, text: `Pasted ${lines.length} rows` });
	};
	const undo = () => {
		const prev = history.current.pop();
		if (prev) update(prev, false);
	};
	const insertRow = () =>
		canEdit && update([...rows.slice(0, act.r), blank(), ...rows.slice(act.r)]);
	const deleteRows = () => canEdit && update(rows.filter((_, i) => i < box.r1 || i > box.r2));

	const onKey = (e: React.KeyboardEvent) => {
		if (editing) return;
		const mod = e.ctrlKey || e.metaKey;
		const k = e.key.toLowerCase();
		if (mod && k === 'z') return (e.preventDefault(), undo());
		if (mod && k === 'c') return (e.preventDefault(), copyBox());
		if (mod && k === 'a')
			return (
				e.preventDefault(),
				setAct({ r: 0, c: 0 }),
				setEnd({ r: rows.length - 1, c: COLS.length - 1 })
			);
		if (mod) return; // Ctrl+V arrives as a paste event
		const moves: Record<string, [number, number]> = {
			ArrowUp: [-1, 0],
			ArrowDown: [1, 0],
			ArrowLeft: [0, -1],
			ArrowRight: [0, 1],
		};
		if (moves[e.key]) {
			e.preventDefault();
			const [dr, dc] = moves[e.key];
			const from = e.shiftKey ? end : act;
			return go(from.r + dr, from.c + dc, e.shiftKey);
		}
		if (e.key === 'Tab') return (e.preventDefault(), go(act.r, act.c + (e.shiftKey ? -1 : 1)));
		if (e.key === 'Enter') return (e.preventDefault(), go(act.r + (e.shiftKey ? -1 : 1), act.c));
		if (e.key === 'Delete' || e.key === 'Backspace') return (e.preventDefault(), clearBox());
		if (e.key === 'F2') return (e.preventDefault(), startEdit(shown(rows[act.r], COLS[act.c])));
		if (e.key === 'Home') return (e.preventDefault(), go(act.r, 0));
		if (e.key.length === 1) {
			e.preventDefault();
			startEdit(e.key);
		}
	};

	const onEditKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') return (e.preventDefault(), commit({ dr: e.shiftKey ? -1 : 1, dc: 0 }));
		if (e.key === 'Tab') return (e.preventDefault(), commit({ dr: 0, dc: e.shiftKey ? -1 : 1 }));
		if (e.key === 'Escape') return (e.preventDefault(), cancel());
		if (e.key === 'ArrowUp' || e.key === 'ArrowDown')
			return (e.preventDefault(), commit({ dr: e.key === 'ArrowUp' ? -1 : 1, dc: 0 }));
	};

	const rowErrors = rows
		.map((r, i) => {
			if (isEmpty(r)) return null;
			if (!(r.person_name || '').trim()) return `Row ${xlRow(i)}: name is missing`;
			const bad = INPUT.find(c => c.num && badNum(r[c.key]));
			if (bad) return `Row ${xlRow(i)}: ${bad.title} must be a number, 0 or more`;
			if (net(r) < 0) return `Row ${xlRow(i)}: deductions are more than earnings`;
			return null;
		})
		.filter(Boolean) as string[];

	// UI rule 3: a disabled button says why.
	const whyNot = !site
		? 'Pick a site first'
		: locked
			? 'Already submitted'
			: !filled.length
				? 'The sheet is empty'
				: rowErrors[0] || null;

	const save = async (submit: boolean) => {
		if (
			submit &&
			!window.confirm(
				`Submit ${site} · ${monthLabel(month)}? It becomes the final sheet and can't be changed after.`
			)
		)
			return;
		setSaving(true);
		setMsg(null);
		try {
			const body = {
				site,
				month,
				submit,
				rows: filled.map(r =>
					Object.fromEntries(
						INPUT.map(c => [c.key, c.num ? (r[c.key] ? Number(r[c.key]) : null) : r[c.key] || null])
					)
				),
			};
			const res = await apiService.put<{
				status: Entry['status'];
				rows: number;
				net_pay_total: number;
			}>('/payroll/entry', body);
			sessionStorage.removeItem(draftKey(site, month));
			setRestored(false);
			setEntry(e =>
				e ? { ...e, status: res.data.status, saved_at: new Date().toISOString() } : e
			);
			setMsg({
				bad: false,
				text: `${submit ? 'Submitted' : 'Saved'}: ${res.data.rows} people, net pay ${full(res.data.net_pay_total)}`,
			});
		} catch (err: unknown) {
			setMsg({ bad: true, text: (err as { message?: string })?.message || 'Could not save' });
		} finally {
			setSaving(false);
		}
	};

	// Status bar: Excel shows Average / Count / Sum when more than one cell is selected.
	const picked: string[] = [];
	for (let r = box.r1; r <= box.r2; r++)
		for (let c = box.c1; c <= box.c2; c++) if (rows[r]) picked.push(shown(rows[r], COLS[c]));
	const nums = picked.filter(v => v !== '' && !isNaN(Number(v))).map(Number);
	const sum = nums.reduce((a, b) => a + b, 0);
	const nonEmpty = picked.filter(v => v !== '').length;

	const totalOf = (c: Col) =>
		c.key === 'person_name'
			? `${filled.length} people`
			: c.num && c.key !== 'working_days'
				? general(filled.reduce((a, r) => a + n(shown(r, c)), 0)).replace(/^0$/, '')
				: '';

	const curCol = COLS[act.c];
	const barValue = editing
		? draft
		: ready
			? (formula(act.r, curCol) ?? shown(rows[act.r] || blank(), curCol))
			: '';
	const status = editing ? 'Edit' : locked ? 'Read-only · submitted' : 'Ready';
	const addSheet = () => {
		const name = window.prompt('Name of the new sheet (site)')?.trim().slice(0, 80);
		if (!name) return;
		if (!allSites.includes(name)) setAdded(a => [...a, name]);
		setSite(name);
	};
	const title = `${monthLabel(month)}${site ? ` · ${site}` : ''}`;
	const stickyLeft = (ci: number) => (ci === 0 ? NUM_W : ci === 1 ? NUM_W + COLS[0].w : undefined);

	return (
		<div
			className='flex flex-col bg-white border border-[#c6c6c6] shadow-sm select-none'
			style={{ fontFamily: XL.font, height: 'calc(100vh - 140px)', margin: '12px' }}
		>
			<style>{`@import url('https://fonts.googleapis.com/css2?family=Carlito:wght@400;700&display=swap');`}</style>
			{/* Title bar */}
			<div
				className='flex items-center h-9 px-3 text-white text-[13px]'
				style={{ background: XL.green }}
			>
				<FileSpreadsheet className='w-4 h-4 mr-2' />
				<span className='font-semibold'>Salary Sheets</span>
				<span className='flex-1 text-center'>
					{title} {entry?.status === 'draft' && '· Draft saved'} {locked && '· Submitted'}
				</span>
				<Link to='/payroll' className='hover:underline opacity-90'>
					Back to Payroll
				</Link>
			</div>

			{/* Ribbon: Home tab, every button works */}
			<div className='bg-[#f3f3f3] border-b border-[#d6d6d6]'>
				<div className='flex text-[13px] px-2'>
					<span
						className='px-3 py-1 border-b-2 font-semibold'
						style={{ borderColor: XL.green, color: XL.green }}
					>
						Home
					</span>
				</div>
				<div className='flex items-stretch bg-[#f9f9f9] border-t border-[#e1e1e1] px-1 py-1 overflow-x-auto'>
					<Group label='Clipboard'>
						<RibbonBtn
							icon={ClipboardPaste}
							label='Paste'
							big
							disabled={!canEdit}
							why='Pick a site first'
							onClick={() =>
								navigator.clipboard
									?.readText()
									.then(pasteText)
									.catch(() => setMsg({ bad: true, text: 'Use Ctrl+V to paste' }))
							}
						/>
						<RibbonBtn icon={ClipboardCopy} label='Copy' disabled={!ready} onClick={copyBox} />
						<RibbonBtn
							icon={Undo2}
							label='Undo'
							disabled={!history.current.length}
							why='Nothing to undo'
							onClick={undo}
						/>
					</Group>
					<Group label='Sheet'>
						<div className='flex flex-col gap-1 text-[12px] text-[#333] px-1'>
							<label className='flex items-center gap-1.5'>
								<span className='w-10'>Site</span>
								<select
									value={site}
									onChange={e => setSite(e.target.value)}
									aria-label='Site'
									className='h-6 w-56 px-1 border border-[#c6c6c6] bg-white outline-none focus:border-[#217346]'
								>
									<option value=''>Pick a site</option>
									{allSites.map(s => (
										<option key={s} value={s}>
											{s}
										</option>
									))}
								</select>
							</label>
							<label className='flex items-center gap-1.5'>
								<span className='w-10'>Month</span>
								<input
									type='month'
									value={month}
									onChange={e => e.target.value && setMonth(e.target.value)}
									aria-label='Wage month'
									className='h-6 w-56 px-1.5 border border-[#c6c6c6] bg-white outline-none focus:border-[#217346]'
								/>
							</label>
						</div>
					</Group>
					<Group label='Cells'>
						<RibbonBtn
							icon={Rows3}
							label='Insert row'
							disabled={!canEdit}
							why='Pick a site first'
							onClick={insertRow}
						/>
						<RibbonBtn
							icon={Trash2}
							label='Delete row'
							disabled={!canEdit}
							why='Pick a site first'
							onClick={deleteRows}
						/>
						<RibbonBtn
							icon={ArrowDownToLine}
							label='Go to end'
							disabled={!ready}
							onClick={() => go(filled.length ? lastFilled + 1 : 0, 1)}
						/>
					</Group>
					<Group label='Payroll'>
						<RibbonBtn
							icon={Save}
							label='Save draft'
							big
							disabled={!!whyNot || saving}
							why={whyNot}
							onClick={() => save(false)}
						/>
						<RibbonBtn
							icon={Send}
							label='Submit'
							big
							disabled={!!whyNot || saving}
							why={whyNot}
							onClick={() => save(true)}
						/>
						{entry && entry.status !== 'new' && (
							<Link
								to={href({ fy: fyOf(month), site, month })}
								className='self-center text-[12px] underline px-1'
								style={{ color: XL.green }}
							>
								See in Payroll
							</Link>
						)}
					</Group>
				</div>
			</div>

			{/* Formula bar */}
			<div className='flex items-center h-7 border-b border-[#d6d6d6] text-[13px]'>
				<div
					className='w-24 h-full flex items-center px-2 border-r border-[#d6d6d6] text-[#333]'
					aria-label='Name box'
				>
					{ready
						? `${letter(box.c1)}${xlRow(box.r1)}${many ? `:${letter(box.c2)}${xlRow(box.r2)}` : ''}`
						: ''}
				</div>
				<div className='px-3 italic text-[#666] font-serif border-r border-[#d6d6d6] h-full flex items-center'>
					fx
				</div>
				<input
					aria-label='Formula bar'
					value={barValue}
					readOnly={!canEdit || !!curCol.calc}
					onFocus={() =>
						!editing && canEdit && !curCol.calc && startEdit(shown(rows[act.r], curCol), true)
					}
					onBlur={() => fromBar && commit()}
					onChange={e => setDraft(e.target.value)}
					onKeyDown={onEditKey}
					className='flex-1 h-full px-2 outline-none bg-white text-[#222] select-text'
				/>
			</div>

			{/* The sheet */}
			<div
				ref={sheet}
				tabIndex={0}
				role='grid'
				aria-label='Salary sheet'
				onKeyDown={onKey}
				onPaste={e => {
					if (editing) return;
					e.preventDefault();
					pasteText(e.clipboardData.getData('text/plain'));
				}}
				className='relative flex-1 overflow-auto outline-none bg-white'
				style={{ fontSize: 15 }}
			>
				{!site && (
					<div className='absolute inset-0 z-30 flex items-center justify-center bg-white/70 text-[15px] text-[#555]'>
						Pick a site in the ribbon (Sheet → Site) to open its salary sheet.
					</div>
				)}
				{site && loading && (
					<div className='absolute inset-0 z-30 flex items-center justify-center bg-white/60'>
						<Loader2 className='w-6 h-6 animate-spin' style={{ color: XL.green }} />
					</div>
				)}
				<table
					className='border-separate'
					style={{
						borderSpacing: 0,
						tableLayout: 'fixed',
						width: NUM_W + COLS.reduce((a, c) => a + c.w, 0),
					}}
				>
					<colgroup>
						<col style={{ width: NUM_W }} />
						{COLS.map(c => (
							<col key={c.key} style={{ width: c.w }} />
						))}
					</colgroup>
					<thead>
						{/* Column letters */}
						<tr style={{ height: ROW_H }}>
							<th
								className='sticky top-0 left-0 z-20'
								style={{
									background: XL.headBg,
									borderRight: `1px solid ${XL.grid}`,
									borderBottom: `1px solid #c6c6c6`,
								}}
							/>
							{COLS.map((c, ci) => {
								const on = ci >= box.c1 && ci <= box.c2;
								return (
									<th
										key={c.key}
										onMouseDown={e => {
											e.preventDefault();
											commit();
											setAct({ r: 0, c: ci });
											setEnd({ r: rows.length - 1, c: ci });
											focusSheet();
										}}
										className={`sticky top-0 font-normal text-[13px] cursor-default ${ci < 2 ? 'z-20' : 'z-10'}`}
										style={{
											left: stickyLeft(ci),
											background: on ? XL.headOn : XL.headBg,
											color: on ? XL.green : '#444',
											fontWeight: on ? 600 : 400,
											borderRight: `1px solid ${XL.grid}`,
											borderBottom: on ? `2px solid ${XL.green}` : '1px solid #c6c6c6',
										}}
									>
										{letter(ci)}
									</th>
								);
							})}
						</tr>
						{/* Row 1: the column names, frozen like Freeze Top Row */}
						<tr style={{ height: ROW_H }}>
							<th
								className='sticky left-0 z-20 font-normal text-[13px] text-[#444]'
								style={{
									top: ROW_H,
									background: XL.headBg,
									borderRight: '1px solid #c6c6c6',
									borderBottom: '1px solid #9b9b9b',
								}}
							>
								1
							</th>
							{COLS.map((c, ci) => (
								<th
									key={c.key}
									className={`sticky px-1 font-bold whitespace-nowrap overflow-hidden ${c.num ? 'text-right' : 'text-left'} ${ci < 2 ? 'z-20' : 'z-10'}`}
									style={{
										top: ROW_H,
										left: stickyLeft(ci),
										background: c.calc ? '#e2efda' : '#ffffff',
										color: '#000',
										borderRight: `1px solid ${XL.grid}`,
										borderBottom: '1px solid #9b9b9b',
									}}
								>
									{c.title}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((r, ri) => {
							const rowOn = ri >= box.r1 && ri <= box.r2;
							return (
								<tr key={ri} style={{ height: ROW_H }}>
									<td
										onMouseDown={e => {
											e.preventDefault();
											commit();
											setAct({ r: ri, c: 0 });
											setEnd({ r: ri, c: COLS.length - 1 });
											focusSheet();
										}}
										className='sticky left-0 z-[5] text-center text-[13px] tabular-nums cursor-default'
										style={{
											background: rowOn ? XL.headOn : XL.headBg,
											color: rowOn ? XL.green : '#444',
											fontWeight: rowOn ? 600 : 400,
											borderRight: rowOn ? `2px solid ${XL.green}` : '1px solid #c6c6c6',
											borderBottom: `1px solid ${XL.grid}`,
										}}
									>
										{xlRow(ri)}
									</td>
									{COLS.map((c, ci) => {
										const isAct = ri === act.r && ci === act.c;
										const sel = inBox(ri, ci);
										const bad = c.num && !c.calc && badNum(r[c.key]);
										const v = shown(r, c);
										return (
											<td
												key={c.key}
												data-cell={`${ri}-${ci}`}
												onMouseDown={e => {
													if (isAct && editing) return;
													e.preventDefault();
													commit();
													if (e.shiftKey) setEnd({ r: ri, c: ci });
													else {
														setAct({ r: ri, c: ci });
														setEnd({ r: ri, c: ci });
													}
													dragging.current = true;
													focusSheet();
												}}
												onMouseEnter={() => dragging.current && setEnd({ r: ri, c: ci })}
												onDoubleClick={() => startEdit(v)}
												title={bad ? 'Not a number' : undefined}
												className={`relative px-1 whitespace-nowrap overflow-hidden ${c.num ? 'text-right tabular-nums' : 'text-left'} ${ci < 2 ? 'sticky z-[4]' : ''}`}
												style={{
													left: stickyLeft(ci),
													color: bad || (c.key === 'net_pay' && net(r) < 0) ? '#c00000' : '#000',
													background: sel && !isAct ? '#e8f2ec' : c.calc ? '#f2f8ee' : '#fff',
													borderRight: `1px solid ${XL.grid}`,
													borderBottom: `1px solid ${XL.grid}`,
													outline: isAct ? `2px solid ${XL.green}` : undefined,
													outlineOffset: -2,
												}}
											>
												{isAct && editing ? (
													<input
														autoFocus={!fromBar}
														value={draft}
														onChange={e => setDraft(e.target.value)}
														onKeyDown={onEditKey}
														onBlur={() => commit()}
														aria-label={`Edit ${letter(ci)}${xlRow(ri)}`}
														className={`absolute inset-0 w-full h-full px-1 outline-none bg-white select-text ${c.num ? 'text-right' : ''}`}
														style={{
															fontFamily: XL.font,
															fontSize: 15,
															border: `2px solid ${XL.green}`,
														}}
													/>
												) : (
													v
												)}
												{bad && (
													<span className='absolute top-0 left-0 w-0 h-0 border-t-[6px] border-r-[6px] border-t-[#c00000] border-r-transparent' />
												)}
												{isAct && !editing && (
													<span
														className='absolute -bottom-[3px] -right-[3px] w-[6px] h-[6px] border border-white z-10'
														style={{ background: XL.green }}
													/>
												)}
											</td>
										);
									})}
								</tr>
							);
						})}
					</tbody>
					{/* Total row, frozen at the bottom so it is always in view */}
					<tfoot>
						<tr style={{ height: ROW_H }}>
							<td
								className='sticky left-0 bottom-0 z-[7] text-center text-[13px] text-[#444]'
								style={{
									background: XL.headBg,
									borderRight: '1px solid #c6c6c6',
									borderTop: '3px double #000',
								}}
							/>
							{COLS.map((c, ci) => (
								<td
									key={c.key}
									className={`sticky bottom-0 px-1 font-bold whitespace-nowrap ${c.num ? 'text-right tabular-nums' : ''} ${ci < 2 ? 'z-[7]' : 'z-[6]'}`}
									title={
										c.num && c.key !== 'working_days'
											? `=SUM(${letter(ci)}2:${letter(ci)}${xlRow(lastFilled)})`
											: undefined
									}
									style={{
										left: stickyLeft(ci),
										background: '#fff',
										borderTop: '3px double #000',
										borderRight: `1px solid ${XL.grid}`,
									}}
								>
									{ci === 0 ? 'Total' : totalOf(c)}
								</td>
							))}
						</tr>
					</tfoot>
				</table>
			</div>

			{/* Sheet tabs = sites, like one workbook per month with a tab per site. "+" adds a sheet. */}
			<div className='flex items-end h-7 bg-[#f3f3f3] border-t border-[#c6c6c6] text-[13px] px-2 gap-0.5 overflow-x-auto'>
				{allSites.map(t => (
					<button
						key={t}
						onClick={() => setSite(t)}
						className='px-4 h-6 -mt-px whitespace-nowrap'
						style={
							t === site
								? {
										background: '#fff',
										color: XL.green,
										fontWeight: 600,
										borderBottom: `2px solid ${XL.green}`,
										boxShadow: '0 1px 2px rgba(0,0,0,.15)',
									}
								: { color: '#444' }
						}
					>
						{t}
					</button>
				))}
				<button
					onClick={addSheet}
					aria-label='New sheet'
					title='New sheet'
					className='w-7 h-6 flex items-center justify-center rounded-full text-[#444] hover:bg-[#e1e1e1] shrink-0'
				>
					<Plus className='w-4 h-4' />
				</button>
			</div>

			{/* Status bar */}
			<div
				className='flex items-center h-6 px-3 text-[12px] text-white gap-4'
				style={{ background: XL.green }}
			>
				<span>{status}</span>
				{msg && <span className={msg.bad ? 'font-semibold text-[#ffd7d7]' : ''}>{msg.text}</span>}
				{!msg && whyNot && site && !locked && filled.length > 0 && (
					<span className='text-[#ffe9a8]'>{whyNot}</span>
				)}
				{!msg && restored && <span>Unsaved changes kept from before</span>}
				{!msg && entry?.status === 'new' && entry.copied_from && (
					<span>Started from {monthLabel(entry.copied_from)}</span>
				)}
				<span className='ml-auto flex gap-5'>
					{many && nums.length > 0 && <span>Average: {general(sum / nums.length)}</span>}
					{many && nonEmpty > 0 && <span>Count: {nonEmpty}</span>}
					{many && nums.length > 0 && <span>Sum: {general(sum)}</span>}
					<span>
						{filled.length} people · Net {full(filled.reduce((a, r) => a + net(r), 0))}
					</span>
				</span>
			</div>
		</div>
	);
};

/** Salaries: only people given Payroll access may open the sheets, even by typing the URL. */
const SalarySheetsPage: React.FC = () =>
	usePayrollAccess() ? <SalaryEntry /> : <NoPayrollAccess />;

export default SalarySheetsPage;
